/**
 * Project Calculator
 * Handles tile/carpet project calculations with unit conversion and waste percentage
 */

class ProjectCalculator {
  constructor(container) {
    this.container = container;
    this.productId = container.dataset.productId;
    this.coverageImperial = parseFloat(container.dataset.coverage);
    if (Number.isNaN(this.coverageImperial)) {
      this.coverageImperial = 0;
    }
    this.coverageMetric = this.coverageImperial * 0.092903;
    this.coverage = this.coverageImperial;
    this.defaultWaste = parseFloat(container.dataset.defaultWaste);
    if (Number.isNaN(this.defaultWaste)) {
      this.defaultWaste = 10;
    }
    this.unitSystem = container.dataset.unitSystem === 'metric' ? 'metric' : 'imperial';
    this.calculationMode = 'total'; // 'dimensions' or 'total'
    this.currentBoxesNeeded = 0;
    this.buttonStatusTimeout = null;
    this.isAddingToCart = false;

    this.localeStrings = {
      enterDimensions: container.dataset.localeEnterDimensions || 'Please enter your room dimensions or total area first.',
      adding: container.dataset.localeAddingText || 'Adding...',
      added: container.dataset.localeAddedText || 'Added to cart!',
      errorAdding: container.dataset.localeErrorText || 'There was an error adding the item to your cart. Please try again.',
    };
    
    this.init();
  }
  
  init() {
    // Get elements
    this.elements = {
      // Toggles
      unitButtons: this.container.querySelectorAll('[data-unit]'),
      modeButtons: this.container.querySelectorAll('[data-mode]'),
      
      // Inputs
      lengthInput: this.container.querySelector('[data-calculator-input="length"]'),
      widthInput: this.container.querySelector('[data-calculator-input="width"]'),
      areaInput: this.container.querySelector('[data-calculator-input="area"]'),
      wasteInput: this.container.querySelector('[data-calculator-input="waste"]'),
      
      // Input containers
      dimensionInputs: this.container.querySelector('[data-input-mode="dimensions"]'),
      totalAreaInput: this.container.querySelector('[data-input-mode="total"]'),
      
      // Displays
      unitDisplays: this.container.querySelectorAll('[data-unit-display]'),
      wasteDisplay: this.container.querySelector('[data-waste-display]'),
      boxesNeededDisplays: this.container.querySelectorAll('[data-boxes-needed]'),
      totalArea: this.container.querySelector('[data-total-area]'),
      areaWithWaste: this.container.querySelector('[data-area-with-waste]'),
      estimatedPrice: this.container.querySelector('[data-estimated-price]'),
      coverageValue: this.container.querySelector('[data-coverage-value]'),
      buttonBoxesNeeded: this.container.querySelector('[data-button-boxes-needed]'),
      buttonDefaultLabel: this.container.querySelector('[data-button-default]'),
      buttonStatusLabel: this.container.querySelector('[data-button-status]'),
      
      // Form
      form: this.container.querySelector('.calculator-form'),
      quantityInput: this.container.querySelector('[data-calculated-quantity]'),
      addToCartBtn: this.container.querySelector('[data-add-to-cart-btn]')
    };
    
    // Set initial active states
    this.setActiveUnit(this.unitSystem);
    this.coverage = this.unitSystem === 'metric' ? this.coverageMetric : this.coverageImperial;
    this.updateCoverageDisplay();
    if (this.elements.dimensionInputs) {
      this.calculationMode = 'dimensions';
      this.setActiveMode('dimensions');
    }
    
    // Bind events
    this.bindEvents();
    
    // Initial calculation
    this.calculate();
  }
  
  bindEvents() {
    // Unit toggle
    this.elements.unitButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        this.toggleUnit(btn.dataset.unit);
      });
    });
    
    // Mode toggle
    this.elements.modeButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        this.toggleMode(btn.dataset.mode);
      });
    });
    
    // Input changes
    const inputs = [
      this.elements.lengthInput,
      this.elements.widthInput,
      this.elements.areaInput,
      this.elements.wasteInput
    ].filter(Boolean);
    
    inputs.forEach(input => {
      input.addEventListener('input', () => this.calculate());
    });
    
    // Waste slider display update
    if (this.elements.wasteInput) {
      this.elements.wasteInput.addEventListener('input', (e) => {
        if (this.elements.wasteDisplay) {
          this.elements.wasteDisplay.textContent = e.target.value;
        }
      });
    }
    
    // Form submission
    if (this.elements.form) {
      this.elements.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.addToCart();
      });
    }
  }
  
  toggleUnit(unit) {
    if (!unit || unit === this.unitSystem) return;
    
    const lengthFactor = unit === 'metric' ? 0.3048 : 3.28084;
    const areaFactor = unit === 'metric' ? 0.092903 : 10.7639;
    
    const convertInput = (input, factor) => {
      if (!input || !input.value) return;
      const numericValue = parseFloat(input.value);
      if (Number.isNaN(numericValue)) {
        input.value = '';
        return;
      }
      input.value = (numericValue * factor).toFixed(2);
    };
    
    convertInput(this.elements.lengthInput, lengthFactor);
    convertInput(this.elements.widthInput, lengthFactor);
    convertInput(this.elements.areaInput, areaFactor);
    
    this.unitSystem = unit;
    this.setActiveUnit(unit);
    this.coverage = unit === 'metric' ? this.coverageMetric : this.coverageImperial;
    this.updateUnitDisplays();
    this.updateCoverageDisplay();
    this.calculate();
  }
  
  setActiveUnit(unit) {
    this.elements.unitButtons.forEach(btn => {
      const isActive = btn.dataset.unit === unit;
      btn.classList.toggle('active', isActive);
      btn.setAttribute('aria-pressed', isActive.toString());
    });
  }
  
  toggleMode(mode) {
    this.calculationMode = mode;
    this.setActiveMode(mode);
    
    // Show/hide input groups
    if (this.elements.dimensionInputs && this.elements.totalAreaInput) {
      if (mode === 'dimensions') {
        this.elements.dimensionInputs.classList.remove('hidden');
        this.elements.totalAreaInput.classList.add('hidden');
        // Clear total area input
        if (this.elements.areaInput) this.elements.areaInput.value = '';
      } else {
        this.elements.dimensionInputs.classList.add('hidden');
        this.elements.totalAreaInput.classList.remove('hidden');
        // Clear dimension inputs
        if (this.elements.lengthInput) this.elements.lengthInput.value = '';
        if (this.elements.widthInput) this.elements.widthInput.value = '';
      }
    }
    
    this.calculate();
  }
  
  setActiveMode(mode) {
    this.elements.modeButtons.forEach(btn => {
      const isActive = btn.dataset.mode === mode;
      btn.classList.toggle('active', isActive);
      btn.setAttribute('aria-pressed', isActive.toString());
    });
  }
  
  updateUnitDisplays() {
    const unitLabels = {
      length: this.unitSystem === 'metric' ? '(m)' : '(ft)',
      area: this.unitSystem === 'metric' ? 'sq m' : 'sq ft'
    };
    
    this.elements.unitDisplays.forEach(display => {
      const type = display?.dataset?.unitDisplay || 'area';
      display.textContent = unitLabels[type] || unitLabels.area;
    });
  }
  
  calculate() {
    let totalArea = 0;
    
    // Calculate based on mode
    if (this.calculationMode === 'dimensions') {
      const length = parseFloat(this.elements.lengthInput?.value);
      const width = parseFloat(this.elements.widthInput?.value);
      if (!Number.isFinite(length) || !Number.isFinite(width) || length <= 0 || width <= 0) {
        this.handleInvalidCalculation();
        return;
      }
      totalArea = length * width;
    } else {
      const inputArea = parseFloat(this.elements.areaInput?.value);
      if (!Number.isFinite(inputArea) || inputArea <= 0) {
        this.handleInvalidCalculation();
        return;
      }
      totalArea = inputArea;
    }
    
    // Get waste percentage
    const wastePercentage = parseFloat(this.elements.wasteInput?.value) || this.defaultWaste;
    
    // Calculate area with waste
    const areaWithWaste = totalArea * (1 + wastePercentage / 100);
    
    // Calculate boxes needed (always round up)
    const boxesNeeded = this.coverage > 0 ? Math.ceil(areaWithWaste / this.coverage) : 0;
    
    // Get product price from data attribute (in cents)
    const priceElement = this.container.querySelector('[data-product-price]');
    let productPrice = 0;
    if (priceElement) {
      productPrice = parseFloat(priceElement.dataset.productPrice) || 0;
    }
    
    const estimatedTotal = boxesNeeded * productPrice;
    
    // Update displays
    this.currentBoxesNeeded = boxesNeeded;
    this.updateDisplays({
      totalArea: totalArea.toFixed(2),
      areaWithWaste: areaWithWaste.toFixed(2),
      boxesNeeded: boxesNeeded,
      estimatedPrice: this.formatMoney(estimatedTotal)
    });
    
    // Update form quantity
    if (this.elements.quantityInput) {
      this.elements.quantityInput.value = boxesNeeded;
    }
  }
  
  updateDisplays(data) {
    // Update all displays
    if (this.elements.totalArea) {
      this.elements.totalArea.textContent = data.totalArea;
    }
    if (this.elements.areaWithWaste) {
      this.elements.areaWithWaste.textContent = data.areaWithWaste;
    }
    if (this.elements.estimatedPrice) {
      this.elements.estimatedPrice.textContent = data.estimatedPrice;
    }
    this.elements.boxesNeededDisplays.forEach(el => {
      el.textContent = data.boxesNeeded;
    });
    this.updateButtonQuantity(data.boxesNeeded);
    // Always update button availability after calculation (unless actively adding to cart)
    if (!this.isAddingToCart) {
      this.updateButtonAvailability(data.boxesNeeded, { force: true });
    }
  }

  updateButtonQuantity(value) {
    const normalizedValue = Number.isFinite(value) ? value : 0;
    if (this.elements.buttonBoxesNeeded) {
      this.elements.buttonBoxesNeeded.textContent = normalizedValue;
    }
  }

  handleInvalidCalculation() {
    this.currentBoxesNeeded = 0;
    if (this.elements.quantityInput) {
      this.elements.quantityInput.value = 0;
    }
    this.updateDisplays({
      totalArea: '0.00',
      areaWithWaste: '0.00',
      boxesNeeded: 0,
      estimatedPrice: this.formatMoney(0)
    });
    this.updateButtonAvailability(0, { force: true });
    if (!this.isAddingToCart) {
      this.resetButtonState();
    }
  }

  updateButtonAvailability(count, { force = false } = {}) {
    if (!this.elements.addToCartBtn) return;
    // Only skip update if status label is visible (during/after cart operations)
    // But always update if force is true or if count changes
    if (!force && this.elements.buttonStatusLabel && !this.elements.buttonStatusLabel.hidden) {
      return;
    }
    // Ensure button is enabled/disabled based on count
    const shouldDisable = count <= 0;
    this.elements.addToCartBtn.disabled = shouldDisable;
    // Also remove/add disabled class for visual consistency
    if (shouldDisable) {
      this.elements.addToCartBtn.classList.add('btn--disabled');
    } else {
      this.elements.addToCartBtn.classList.remove('btn--disabled');
    }
  }

  updateCoverageDisplay() {
    if (!this.elements.coverageValue) return;
    const coverage =
      this.unitSystem === 'metric' ? this.coverageMetric : this.coverageImperial;
    const formattedCoverage = parseFloat(coverage.toFixed(2));
    this.elements.coverageValue.textContent = Number.isNaN(formattedCoverage)
      ? '0'
      : formattedCoverage.toString();
  }
  
  formatMoney(cents) {
    // Format money based on Shopify's money format
    // This is a basic implementation - you can enhance it with shop.money_format
    const dollars = cents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(dollars);
  }
  
  async addToCart() {
    const quantity = parseInt(this.elements.quantityInput?.value, 10) || 0;
    
    if (quantity <= 0) {
      this.showCartMessage(this.localeStrings.enterDimensions, 'error');
      this.resetButtonState();
      return;
    }
    
    const variantInput = this.elements.form?.querySelector('input[name="id"]');
    const variantId = variantInput?.value;
    
    if (!variantId) {
      this.showCartMessage(this.localeStrings.errorAdding, 'error');
      return;
    }
    
    if (this.isAddingToCart) {
      return;
    }
    
    this.isAddingToCart = true;
    this.setButtonState('loading');
    
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity
            }
          ]
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || 'Failed to add to cart');
      }
      
      await response.json();
      
      const cartResponse = await fetch('/cart.js', {
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (!cartResponse.ok) {
        throw new Error('Failed to fetch cart data');
      }
      
      const cartData = await cartResponse.json();
      const itemCount = cartData?.item_count || 0;
      
      this.updateCartIconBubble(itemCount);
      this.updateCartCountBadges(itemCount);
      this.showCartMessage(this.localeStrings.added, 'success');
      this.setButtonState('success');
      this.dispatchCartUpdated(cartData, variantId, quantity);
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showCartMessage(this.localeStrings.errorAdding, 'error');
      this.setButtonState('error');
    } finally {
      this.isAddingToCart = false;
    }
  }

  setButtonState(state) {
    if (!this.elements.addToCartBtn || !this.elements.buttonDefaultLabel || !this.elements.buttonStatusLabel) {
      return;
    }

    const button = this.elements.addToCartBtn;
    const defaultLabel = this.elements.buttonDefaultLabel;
    const statusLabel = this.elements.buttonStatusLabel;

    clearTimeout(this.buttonStatusTimeout);

    const showStatus = (text) => {
      statusLabel.textContent = text;
      statusLabel.hidden = false;
      defaultLabel.hidden = true;
      button.disabled = true;
    };

    switch (state) {
      case 'loading':
        button.classList.add('loading');
        showStatus(this.localeStrings.adding);
        break;
      case 'success':
        button.classList.remove('loading');
        showStatus(this.localeStrings.added);
        this.buttonStatusTimeout = setTimeout(() => {
          this.resetButtonState();
        }, 2000);
        break;
      case 'error':
        button.classList.remove('loading');
        showStatus(this.localeStrings.errorAdding);
        this.buttonStatusTimeout = setTimeout(() => {
          this.resetButtonState();
        }, 3000);
        break;
      default:
        this.resetButtonState();
    }
  }

  resetButtonState() {
    if (!this.elements.addToCartBtn || !this.elements.buttonDefaultLabel || !this.elements.buttonStatusLabel) {
      return;
    }
    this.elements.addToCartBtn.classList.remove('loading');
    this.elements.buttonStatusLabel.hidden = true;
    this.elements.buttonDefaultLabel.hidden = false;
    this.updateButtonAvailability(this.currentBoxesNeeded, { force: true });
  }

  showCartMessage(message, type = 'info') {
    let messageEl = document.querySelector('[data-calculator-cart-message]');
    if (!messageEl) {
      messageEl = document.createElement('div');
      messageEl.setAttribute('data-calculator-cart-message', '');
      messageEl.setAttribute('aria-live', 'polite');
      messageEl.setAttribute('role', 'alert');
      messageEl.className = 'fixed top-4 right-4 p-4 rounded-lg shadow-lg z-50 transition-all duration-300';
      document.body.appendChild(messageEl);
    }

    messageEl.textContent = message;

    let bgColor;
    let textColor = 'text-white';

    switch (type) {
      case 'success':
        bgColor = 'bg-green-500';
        break;
      case 'error':
        bgColor = 'bg-red-500';
        break;
      default:
        bgColor = 'bg-blue-500';
    }

    messageEl.className = `fixed top-4 right-4 p-4 rounded-lg shadow-lg z-50 transition-all duration-300 ${bgColor} ${textColor}`;

    const hideDelay = type === 'error' ? 5000 : 3000;
    setTimeout(() => {
      if (messageEl.parentNode) {
        messageEl.style.opacity = '0';
        messageEl.style.transform = 'translateX(100%)';
        setTimeout(() => {
          if (messageEl.parentNode) {
            messageEl.parentNode.removeChild(messageEl);
          }
        }, 300);
      }
    }, hideDelay);
  }

  updateCartIconBubble(itemCount) {
    const cartIcons = document.querySelectorAll('[id^="cart-icon-bubble"]');

    cartIcons.forEach(cartIcon => {
      let countBadge = cartIcon.querySelector('.menu-single-row__cart-count');

      if (itemCount > 0) {
        if (!countBadge) {
          countBadge = document.createElement('sup');
          countBadge.className = 'menu-single-row__cart-count';
          const icon = cartIcon.querySelector('[data-lucide]');
          if (icon) {
            cartIcon.insertBefore(countBadge, icon);
          } else {
            cartIcon.insertBefore(countBadge, cartIcon.firstChild);
          }
        }
        countBadge.textContent = itemCount;
      } else if (countBadge) {
        countBadge.remove();
      }
    });
  }

  updateCartCountBadges(itemCount) {
    const cartCountElements = document.querySelectorAll('[data-cart-count]');
    cartCountElements.forEach(el => {
      el.textContent = itemCount;
    });
  }

  dispatchCartUpdated(cartData, variantId, quantity) {
    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: {
          source: 'project-calculator',
          productVariantId: variantId,
          cartData,
          itemCount: cartData?.item_count || 0,
          quantityAdded: quantity
        }
      })
    );
  }
}

// Initialize all calculators on page
document.addEventListener('DOMContentLoaded', () => {
  const calculators = document.querySelectorAll('[data-calculator]');
  calculators.forEach(calculator => {
    new ProjectCalculator(calculator);
  });
});

